import React, { useState } from "react";
import { Modal, Backdrop, Fade, Box, Typography, Button, TextField, useMediaQuery, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../api/config/axiosConfig";

const SuperAdminVerifationModal = ({ open, onClose, onSuccess, onError }) => {
  const inputRefs = Array.from({ length: 6 }, () => React.createRef());
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const isConfirmButtonDisabled = digits.some(d => d.length === 0);

  const handleChange = (index, value) => {
    if (value.match(/^\d{0,1}$/)) {
      const updatedDigits = [...digits];
      updatedDigits[index] = value;
      setDigits(updatedDigits);

      if (value === "" && index > 0) {
        inputRefs[index - 1].current.focus();
      } else if (index < 5 && value !== "") {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const confirm2Fa = () => {
    const code = digits.join("");
    axiosInstance.post(`/auth/confirm_otp_to_super_admin`, { code })
      .then(() => {
        onSuccess();
        setDigits(["", "", "", "", "", ""]);
      })
      .catch((error) => {
        console.error("Verification error:", error);
        onError();
      });
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isSmallScreen ? '90%' : 460,
    bgcolor: 'background.paper',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    borderRadius: '24px',
    p: 4,
    outline: 'none',
    border: '1px solid rgba(0, 0, 0, 0.05)',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ 
        timeout: 500,
        sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' }
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box textAlign="center" mb={4}>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a', fontFamily: 'Outfit, sans-serif', mb: 1 }}>
              Super Admin Verification
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'Inter, sans-serif', maxWidth: '300px', margin: '0 auto' }}>
              Please enter the 6-digit security code from the Super Admin to authorize this action.
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center" gap={1.5} mb={5}>
            {digits.map((digit, index) => (
              <TextField
                key={index}
                inputRef={inputRefs[index]}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ 
                  style: { 
                    textAlign: 'center', 
                    fontSize: '1.25rem', 
                    fontWeight: '700',
                    padding: '12px 0',
                    color: '#0f172a',
                    fontFamily: 'Outfit, sans-serif'
                  },
                  maxLength: 1
                }}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                sx={{ 
                  width: isSmallScreen ? '42px' : '58px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#00aff0', borderWidth: '2px' },
                  }
                }}
              />
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isConfirmButtonDisabled}
            onClick={confirm2Fa}
            sx={{
              py: 2,
              borderRadius: '14px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: '700',
              fontFamily: 'Outfit, sans-serif',
              background: 'linear-gradient(135deg, #00aff0 0%, #007bbd 100%)',
              boxShadow: '0 10px 20px rgba(0,175,240,0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #009ce0 0%, #006da8 100%)',
                boxShadow: '0 12px 24px rgba(0,175,240,0.3)',
              },
              '&.Mui-disabled': {
                background: '#f1f5f9',
                color: '#94a3b8'
              }
            }}
          >
            Verify and Continue
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SuperAdminVerifationModal;
